/**
 * Full-page customer profile: personal and company profile form.
 * Replaces the profile update modal with a dedicated page for more screen space.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  User2,
  Building2,
  Mail,
  Phone,
  Globe,
  FileText,
  Target,
} from 'lucide-react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import type { ProfileUpdateFormData } from '@/components/ProfileUpdateFormModal';

interface CustomerProfilePageProps {
  userId: string;
  defaultDisplayName?: string;
  defaultEmail?: string | null;
  showNotify: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const emptyForm: ProfileUpdateFormData = {
  displayName: '',
  companyName: '',
  contactEmail: '',
  contactPhone: '',
  websiteUrl: '',
  capabilitiesSummary: '',
  pastPerformanceNotes: '',
};

export function CustomerProfilePage({
  userId,
  defaultDisplayName = '',
  defaultEmail = '',
  showNotify,
}: CustomerProfilePageProps): JSX.Element {
  const [form, setForm] = useState<ProfileUpdateFormData>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setLoading(true);

    const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'main');

    getDoc(profileRef)
      .then((snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data() as Record<string, unknown>;
          setForm({
            displayName: (d.displayName as string) ?? defaultDisplayName,
            companyName: (d.companyName as string) ?? '',
            contactEmail: (d.contactEmail as string) ?? defaultEmail ?? '',
            contactPhone: (d.contactPhone as string) ?? '',
            websiteUrl: (d.websiteUrl as string) ?? '',
            capabilitiesSummary: (d.capabilitiesSummary as string) ?? '',
            pastPerformanceNotes: (d.pastPerformanceNotes as string) ?? '',
          });
        } else {
          setForm({
            ...emptyForm,
            displayName: defaultDisplayName,
            contactEmail: defaultEmail ?? '',
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[CustomerProfilePage] Failed to load profile:', err);
          setForm({
            ...emptyForm,
            displayName: defaultDisplayName,
            contactEmail: defaultEmail ?? '',
          });
          showNotify('Could not load profile. You can still save.', 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, defaultDisplayName, defaultEmail, showNotify]);

  const handleChange =
    (field: keyof ProfileUpdateFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const displayName = form.displayName.trim();
    if (!displayName) {
      showNotify('Display name is required.', 'error');
      return;
    }

    setSaving(true);
    try {
      const profileRef = doc(db, 'artifacts', appId, 'users', userId, 'profile', 'main');
      const existing = await getDoc(profileRef);
      const existingPhotoUrl = existing.exists()
        ? (existing.data() as Record<string, unknown>).photoUrl
        : null;
      await setDoc(
        profileRef,
        {
          displayName,
          photoUrl: existingPhotoUrl ?? null,
          companyName: form.companyName.trim() || null,
          contactEmail: form.contactEmail.trim() || null,
          contactPhone: form.contactPhone.trim() || null,
          websiteUrl: form.websiteUrl.trim() || null,
          capabilitiesSummary: form.capabilitiesSummary.trim() || null,
          pastPerformanceNotes: form.pastPerformanceNotes.trim() || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      showNotify('Profile updated successfully.', 'success');
    } catch (err) {
      console.error('[CustomerProfilePage] Failed to save profile:', err);
      showNotify('Failed to save profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center" role="main" aria-busy="true">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={40} className="animate-spin text-smb-primary" aria-hidden="true" />
          <p className="text-sm text-gray-600">Loading profile…</p>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" className="bg-gray-50 min-h-screen" role="main" aria-label="Customer profile">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm font-medium text-smb-primary hover:text-smb-primary-mid transition-colors mb-6"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          Back to dashboard
        </Link>

        <div className="rounded-2xl border border-smb-slate-100 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-5 sm:px-8 sm:py-6 border-b border-gray-200">
            <h1 className="text-2xl sm:text-3xl font-bold text-smb-ink">Update profile</h1>
            <p className="mt-1 text-sm text-smb-slate-600">
              Keep your personal and company details current for better recommendations.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            <div className="space-y-10">
              {/* Personal profile */}
              <section aria-labelledby="personal-profile-heading">
                <div className="flex items-center gap-2 mb-3">
                  <User2 size={20} className="text-smb-primary" aria-hidden="true" />
                  <h2 id="personal-profile-heading" className="text-base font-semibold uppercase tracking-wide text-gray-700">
                    Personal profile
                  </h2>
                </div>
                <p className="text-sm text-gray-500 mb-4">Your name as shown in the app.</p>
                <div className="max-w-md">
                  <label htmlFor="profile-displayName" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Display name *
                  </label>
                  <div className="relative">
                    <User2
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      aria-hidden="true"
                    />
                    <input
                      id="profile-displayName"
                      type="text"
                      required
                      value={form.displayName}
                      onChange={handleChange('displayName')}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your name"
                    />
                  </div>
                </div>
              </section>

              <div className="border-t border-gray-200" role="separator" />

              {/* Company profile */}
              <section aria-labelledby="company-profile-heading">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 size={20} className="text-smb-primary" aria-hidden="true" />
                  <h2 id="company-profile-heading" className="text-base font-semibold uppercase tracking-wide text-gray-700">
                    Company profile
                  </h2>
                </div>
                <p className="text-sm text-gray-500 mb-4">Business details used for recommendations and matching.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="profile-companyName" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Company name
                    </label>
                    <div className="relative">
                      <Building2
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        aria-hidden="true"
                      />
                      <input
                        id="profile-companyName"
                        type="text"
                        value={form.companyName}
                        onChange={handleChange('companyName')}
                        className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Acme Federal Solutions"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="profile-contactEmail" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Company contact email
                    </label>
                    <div className="relative">
                      <Mail
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        aria-hidden="true"
                      />
                      <input
                        id="profile-contactEmail"
                        type="email"
                        value={form.contactEmail}
                        onChange={handleChange('contactEmail')}
                        className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="contact@company.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="profile-contactPhone" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Company contact phone
                    </label>
                    <div className="relative">
                      <Phone
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        aria-hidden="true"
                      />
                      <input
                        id="profile-contactPhone"
                        type="tel"
                        value={form.contactPhone}
                        onChange={handleChange('contactPhone')}
                        className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="profile-websiteUrl" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Website
                    </label>
                    <div className="relative">
                      <Globe
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        aria-hidden="true"
                      />
                      <input
                        id="profile-websiteUrl"
                        type="url"
                        value={form.websiteUrl}
                        onChange={handleChange('websiteUrl')}
                        className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://www.example.com"
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="profile-capabilitiesSummary" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Capabilities / NAICS
                  </label>
                  <div className="relative">
                    <Target size={18} className="absolute left-3 top-3 text-gray-400" aria-hidden="true" />
                    <textarea
                      id="profile-capabilitiesSummary"
                      rows={4}
                      value={form.capabilitiesSummary}
                      onChange={handleChange('capabilitiesSummary')}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      placeholder="e.g. NAICS codes, core capabilities, contract vehicles"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="profile-pastPerformanceNotes" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Past performance notes
                  </label>
                  <div className="relative">
                    <FileText size={18} className="absolute left-3 top-3 text-gray-400" aria-hidden="true" />
                    <textarea
                      id="profile-pastPerformanceNotes"
                      rows={4}
                      value={form.pastPerformanceNotes}
                      onChange={handleChange('pastPerformanceNotes')}
                      className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      placeholder="Relevant past performance or differentiators"
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-smb-primary hover:bg-smb-primary-mid px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  'Save profile'
                )}
              </button>
              <Link
                to="/dashboard"
                className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
